import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '@ortho-next/auth-core';
import { map } from 'rxjs/operators';
import { APP_CONFIG } from '../../core';
import { CaseService, Guide, GuideService, UserService } from '../../services';
import { BaseComponent } from '../base';
import { AnatomicalSideLabelPipe, BoneTypeLabelPipe, PlanTypeLabelPipe, ReplaceLongLegPipe } from '../pipe';


/**
 * Base component for navbar.
 */
@Component({
	selector: 'base-navbar',
	templateUrl: './base-navbar.component.html',
	imports: [
		NgbDropdownModule,
		BoneTypeLabelPipe,
		PlanTypeLabelPipe,
		ReplaceLongLegPipe,
		AnatomicalSideLabelPipe
	],
	changeDetection: ChangeDetectionStrategy.Eager,
})
export class BaseNavbarComponent extends BaseComponent {

	protected readonly config = inject(APP_CONFIG);
	private readonly userSrv = inject(UserService);
	private readonly caseSrv = inject(CaseService);
	private readonly authSrv = inject(AuthService);
	private readonly guideSrv = inject(GuideService);


	plan = toSignal(this.caseSrv.getCurrentCase());
	isReadOnly = toSignal(this.caseSrv.isReadOnly());
	isWatermarkVisible = toSignal(this.userSrv.isDemoUser());
	username = toSignal(this.userSrv.getUserName().pipe(map(name => `${name.firstName} ${name.lastName}`)), { initialValue: '' });
	planStepLabel = computed(() => {
		if (!this.plan()) return '';
		return this.plan()!.isPostOperative ? this.labels().NAVBAR_COMPONENT_PLAN_STEP_POSTOP : this.labels().NAVBAR_COMPONENT_PLAN_STEP_PREOP;
	});

	guideList: Guide[] = [];
	readonly isSaveVisible: boolean = false;
	readonly envName = this.config.envName;
	readonly buildVersion = this.config.buildVer;
	readonly eifuSiteUrl = this.config.eifuSite;

	/**
	* Open about page.
	*/
	openAboutPage(): void {
		window.open(`${this.config.aboutSite}${this.langSrv.currentLang}`, '_blank');
	}

	/**
	* Open legal terms page.
	*/
	openLegalTerms(): void {
		this.userSrv.getUserCountry().subscribe(code => {
			window.open(`${this.config.legalTermsSite}?countryCode=${code}`);
		});
	}

	/**
	* Logout.
	*/
	logout(): void {
		this.authSrv.singleSignOut();
	}

	/**
	 * Get Guide list when open dropdown the first time.
	 */
	getGuideList(open: boolean): void {
		if (!!open && (!this.guideList || this.guideList.length === 0)) {
			this.guideSrv.getGuideList().subscribe(list => this.guideList = list);
		}
	}

	/**
	 * Open guide page.
	 */
	openGuide(path: string): void {
		const guideUrl = this.guideSrv.getGuideUrl(path);
		window.open(guideUrl, '_blank');
	}

	/**
	* Open modal of help info.
	*/
	openHelpModal(): void {
		throw new Error('Missing help modal implementation...');
	}

	/**
	 * Open modal of confirmation before exit.
	*/
	openClosingModal(): void {
		throw new Error('Missing closing modal implementation...');
	}

	/**
	 * Persist model scene.
	*/
	save(): void {
		throw new Error('Missing save implementation...');
	}

}
